/**
 * Stub: expo-notifications
 *
 * On web/desktop, push notifications are delivered via the browser's Push API
 * and the service-worker.js file. This stub exposes the same surface as
 * expo-notifications so the existing codebase compiles without changes.
 *
 * Scheduling is silently ignored — the backend is responsible for scheduling
 * reminders and delivering them via web-push. Subscriptions are managed via
 * the existing web-push endpoints already in the backend.
 */

const noop = () => Promise.resolve(null);
const noopEmitter = { remove: () => {} };

// ── Notification handler (foreground) ────────────────────────────────────────
export const setNotificationHandler = () => {};

// ── Permissions ───────────────────────────────────────────────────────────────
// Report what the browser ACTUALLY holds. This used to answer a hardcoded
// `undetermined`, which made every caller believe an ask was still possible —
// including on a browser that had already granted or permanently blocked
// notifications. `requestPermissionsAsync` below has always read the real
// `Notification` API; only the read side was invented.
//
// Browser vocabulary → expo's: 'default' is "not asked yet", which is expo's
// 'undetermined'. 'granted' and 'denied' carry over unchanged, and only
// 'default' can still raise a prompt.
//
// No `Notification` at all (an insecure origin, or a browser without the API)
// is reported the same way requestPermissionsAsync already reports it: denied,
// with no ask available.
export const getPermissionsAsync = () => {
  if (typeof Notification === 'undefined') {
    return Promise.resolve({ status: 'denied', granted: false, canAskAgain: false, expires: 'never', ios: null, android: null });
  }
  const permission = Notification.permission;
  const status = permission === 'default' ? 'undetermined' : permission;
  return Promise.resolve({
    status,
    granted: permission === 'granted',
    canAskAgain: permission === 'default',
    expires: 'never',
    ios: null, android: null,
  });
};

export const requestPermissionsAsync = () => {
  if (typeof Notification === 'undefined') return Promise.resolve({ granted: false, status: 'denied' });
  return Notification.requestPermission().then((permission) => ({
    granted: permission === 'granted',
    status: permission,
    canAskAgain: permission === 'default',
    expires: 'never',
    ios: null, android: null,
  }));
};

// ── Token / device push token ─────────────────────────────────────────────────
// On web we use the PushSubscription (managed via service worker), not a token.
// Return null so callers fall through to the web-push subscription flow.
export const getExpoPushTokenAsync  = () => Promise.resolve(null);
export const getDevicePushTokenAsync = () => Promise.resolve(null);

// ── Scheduling ────────────────────────────────────────────────────────────────
// Scheduling is a no-op on web — the backend handles delivery via web-push.
export const scheduleNotificationAsync    = noop;
export const cancelScheduledNotificationAsync = noop;
export const cancelAllScheduledNotificationsAsync = noop;
export const getAllScheduledNotificationsAsync = () => Promise.resolve([]);

// ── Presented notifications ───────────────────────────────────────────────────
export const getPresentedNotificationsAsync   = () => Promise.resolve([]);
export const dismissNotificationAsync          = noop;
export const dismissAllNotificationsAsync      = noop;

// ── Categories (action buttons) ───────────────────────────────────────────────
export const setNotificationCategoryAsync    = () => Promise.resolve(null);
export const getNotificationCategoriesAsync  = () => Promise.resolve([]);
export const deleteNotificationCategoryAsync = noop;

// ── Channels (Android) ────────────────────────────────────────────────────────
export const setNotificationChannelAsync    = noop;
export const getNotificationChannelAsync    = () => Promise.resolve(null);
export const getNotificationChannelsAsync   = () => Promise.resolve([]);
export const deleteNotificationChannelAsync = noop;

// ── Last notification response ────────────────────────────────────────────────
// On native this returns the last tapped notification; on web we use the
// service-worker notificationclick event instead, so always return null here.
export const getLastNotificationResponse = () => null;

// ── Listeners ─────────────────────────────────────────────────────────────────
export const addNotificationReceivedListener           = () => noopEmitter;
export const addNotificationResponseReceivedListener   = () => noopEmitter;
export const addPushTokenListener                      = () => noopEmitter;
export const removeNotificationSubscription            = () => {};

// ── Badge ─────────────────────────────────────────────────────────────────────
export const getBadgeCountAsync = () => Promise.resolve(0);
export const setBadgeCountAsync = noop;

// ── Android importance constants ──────────────────────────────────────────────
export const AndroidImportance = { NONE: 0, MIN: 1, LOW: 2, DEFAULT: 3, HIGH: 4, MAX: 5 };
export const AndroidNotificationVisibility = { UNKNOWN: 0, PUBLIC: 1, PRIVATE: 2, SECRET: -1 };
export const IosAlertStyle = { NONE: 'none', BANNER: 'banner', ALERT: 'alert' };

export default {
  setNotificationHandler,
  getPermissionsAsync, requestPermissionsAsync,
  getExpoPushTokenAsync, getDevicePushTokenAsync,
  scheduleNotificationAsync, cancelScheduledNotificationAsync,
  cancelAllScheduledNotificationsAsync, getAllScheduledNotificationsAsync,
  getPresentedNotificationsAsync, dismissNotificationAsync, dismissAllNotificationsAsync,
  setNotificationCategoryAsync, getNotificationCategoriesAsync, deleteNotificationCategoryAsync,
  setNotificationChannelAsync, getNotificationChannelAsync, getNotificationChannelsAsync, deleteNotificationChannelAsync,
  addNotificationReceivedListener, addNotificationResponseReceivedListener,
  addPushTokenListener, removeNotificationSubscription,
  getBadgeCountAsync, setBadgeCountAsync,
  AndroidImportance, AndroidNotificationVisibility, IosAlertStyle,
};
